import type { Message } from "../../../types";
import type { AdminSessionStatus } from "../../../services/adminSessionsService";

export const STATUS_LABEL: Record<AdminSessionStatus, string> = {
  not_submitted: "Not submitted",
  submitted: "Submitted",
  graded: "Graded",
};

export const STATUS_TONE: Record<AdminSessionStatus, string> = {
  not_submitted: "bg-outline-variant/30 text-on-surface-variant border-outline-variant/50",
  submitted: "bg-secondary/10 text-secondary border-secondary/30",
  graded: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

/** Short local date-time for tables and headers, e.g. "Oct 27, 2026, 2:02 PM". */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export interface PersonaEntry {
  id: string;
  name: string;
  role: string;
  avatarUrl?: string;
  messageCount: number;
}

/**
 * Personas to list on the session page: the scenario's own, in authoring
 * order, then any persona id that appears in the transcript but no longer in
 * the scenario, so no conversation is unreachable.
 */
export function personaEntries(
  personas: { id: string; name: string; role: string; avatarUrl?: string }[],
  messages: Message[],
): PersonaEntry[] {
  const counts = new Map<string, number>();
  for (const m of messages) counts.set(m.personaId, (counts.get(m.personaId) ?? 0) + 1);

  const known = new Set(personas.map((p) => p.id));
  const entries: PersonaEntry[] = personas.map((p) => ({
    ...p,
    messageCount: counts.get(p.id) ?? 0,
  }));
  for (const [id, count] of counts) {
    if (known.has(id)) continue;
    entries.push({
      id,
      name: `Unknown persona (${id.slice(0, 8)})`,
      role: "Not in this scenario",
      messageCount: count,
    });
  }
  return entries;
}

export function defaultPersonaId(entries: PersonaEntry[]): string | null {
  return (entries.find((e) => e.messageCount > 0) ?? entries[0])?.id ?? null;
}
